import { createSlice } from '@reduxjs/toolkit';
import { openSnackbar, setAgyleHeaderLoading, setSnackbar } from './globals';
import {
    bulkCreateAppointmentSessionObjects,
    createAppointmentSessionObject,
    groupClinicianFilter,
} from '../../pages/appointments/utils/appointmentSessionUtils';
import {
    APPOINTMENT_MODALS,
    CONTEXT_OPTIONS,
    PROHIBITED_SLOT_STATUSES,
    SLOT_MANIPULATION_OPTIONS,
    SLOT_STATUSES,
} from '../../pages/appointments/utils/constants/appointmentSessionConstants';
import moment from 'moment';
import _ from 'lodash';
import { setLoading } from './patient';
import { getSlotsForSession } from '../../pages/appointments/components/utils/rruleUtils';
import API from '../../app/api';
import { getConsultantName } from '../../pages/centralPage/helper';
import { AppointmentSlotStatus } from '../../pages/appointments/components/utils/slotUtils';
import { sendUserAudit } from './user';
import { AUDIT_TYPES } from '../../util/auditUtil';
import { PATIENT_TAGS } from '../../util/patientUtil';

export const VIEW_FILTER_STATES = {
    ALL: 'ALL',
    PTS: 'PTS',
    SLOTS: 'SLOTS',
};

export const INIT_FILTER_STATE = {
    startDate: moment().startOf('month'),
    endDate: moment().endOf('month'),
    locations: [],
    clinics: [],
    sessions: [],
    specialties: [],
    clinicians: [],
    statuses: [],
    practitionerRoles: [],
    view: VIEW_FILTER_STATES.ALL,
};

const INIT_MODALS_OPEN = {
    [APPOINTMENT_MODALS.ASSIGN_SESSION]: false,
    [APPOINTMENT_MODALS.ADD_EDIT_SESSION]: false,
    [APPOINTMENT_MODALS.CHANGE_REVIEW_STATUS]: false,
    [APPOINTMENT_MODALS.PATIENT_REGISTRY]: false,
    [APPOINTMENT_MODALS.STATUS_TIMELINE]: false,
};

const DAY_CALENDAR_HELPER_STATE = {
    [SLOT_MANIPULATION_OPTIONS?.LOCK_SLOT]: false,
    [SLOT_MANIPULATION_OPTIONS?.CREATE_SLOT]: false,
    [SLOT_MANIPULATION_OPTIONS?.ASSIGN]: false,
};

export const INIT_COUNTS_STATES = {
    total: 0,
    beeingSeen: 0,
    nextBeeingSeen: 0,
    notRegistered: 0,
    arrived: 0,
    departed: 0,
    doNotAttend: 0,
    notLocked: 0,
    sessions: 0,
};

export const APPOINTMENT_BOOK_STATE_INIT = {
    reason: '',
    linkToActivity: false,
    type: null,
    linkedAttendance: undefined,
};

export const appointmentSlice = createSlice({
    name: 'appointments',
    initialState: {
        patient: null,
        view: 'patient',
        context: 'swimLanes',
        map: '',
        dayViewOpen: false,
        showWidgets: true,
        modalHelperState: null,
        modalsOpen: INIT_MODALS_OPEN,
        dayCalendarShowAdditionalTiles: false,
        dayCalendarHelperState: DAY_CALENDAR_HELPER_STATE,
        sessions: [],
        sessionsCache: {},
        appointments: [],
        appliedFilters: INIT_FILTER_STATE,
        filters: INIT_FILTER_STATE,
        appointmentBooksState: APPOINTMENT_BOOK_STATE_INIT,
        daySlotsUnfiltered: [],
        daySlots: [],
        daySlotsGrouped: [],
        hiddenGroups: {},
        monthStats: {},
        filterLocations: [],
        filterClinicNames: [],
        filterSessionNames: [],
        filterSpecialties: [],
        filterClinicians: [],
        filterPractitionerRoles: [],
        currentDay: moment().startOf('day'),
        counts: INIT_COUNTS_STATES,
        listSort: null,
        isBarsContext: false,
        contextCache: {},
        practitionerRoles: [],
        facility: null,
        autoLocationSelect: null,
    },
    reducers: {
        setPatient: (state, action) => {
            state.patient = action.payload;
        },
        setSelectedDate: (state, action) => {
            state.currentDay = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
        setAppliedFilters: (state, action) => {
            state.appliedFilters = action.payload;
        },
        setMap: (state, map) => {
            state.map = map.payload;
        },
        setDayViewOpen: (state, open) => {
            // state.dayViewOpen = open.payload;
            state.context = 'swimLanes';
        },
        setView: (state, view) => {
            state.view = view.payload;
        },
        setContext: (state, view) => {
            state.context = view.payload;
        },
        setModalOpen: (state, modal) => {
            state.modalsOpen = {
                ...state.modalsOpen,
                ...modal.payload,
            };
        },
        setModalHelperState: (state, helperState) => {
            state.modalHelperState = helperState.payload;
        },
        setDayCalendarShowAdditionalTiles: (state, show) => {
            state.dayCalendarShowAdditionalTiles = show.payload;
        },
        setDayCalendarHelperState: (state, show) => {
            state.dayCalendarHelperState = {
                ...state.dayCalendarHelperState,
                ...show.payload,
            };
        },
        setShowWidgets: (state, modal) => {
            state.showWidgets = modal.payload;
        },
        setSessions: (state, action) => {
            state.sessions = action.payload;
        },
        setDaySlots: (state, action) => {
            state.daySlots = action.payload;
        },
        setDaySlotsGrouped: (state, action) => {
            state.daySlotsGrouped = action.payload;
        },
        setHiddenGroups: (state, action) => {
            state.hiddenGroups = action.payload;
        },
        setDaySlotsUnfiltered: (state, action) => {
            state.daySlotsUnfiltered = action.payload;
        },
        setFilterLocations: (state, action) => {
            state.filterLocations = action.payload;
        },
        setFilterClinicNames: (state, action) => {
            state.filterClinicNames = action.payload;
        },
        setFilterSessionNames: (state, action) => {
            state.filterSessionNames = action.payload;
        },
        setFilterSpecialties: (state, action) => {
            state.filterSpecialties = action.payload;
        },
        setFilterClinicians: (state, action) => {
            state.filterClinicians = action.payload;
        },
        setFilterPractitionerRoles: (state, action) => {
            state.filterPractitionerRoles = action.payload;
        },
        setCounts: (state, action) => {
            state.counts = action.payload;
        },
        setAppointments: (state, action) => {
            state.appointments = action.payload;
        },
        setMonthStats: (state, action) => {
            state.monthStats = action.payload;
        },
        setSessionsCache: (state, action) => {
            state.sessionsCache = action.payload;
        },
        setListSort: (state, action) => {
            state.listSort = action.payload;
        },
        setBarsContext: (state, action) => {
            state.isBarsContext = action.payload;
        },
        setContextCache: (state, action) => {
            state.contextCache = action.payload;
        },
        setPractitionerRoles: (state, action) => {
            state.practitionerRoles = action.payload;
        },
        setFacility: (state, action) => {
            state.facility = action.payload;
        },
        setAutoLocationSelect: (state, action) => {
            state.autoLocationSelect = action.payload;
        },
        setAppointmentBooksState: (state, action) => {
            state.appointmentBooksState = {
                ...state.appointmentBooksState,
                ...action.payload,
            };
        },
    },
});

export const appointmentsDaySlotsGrouped = (state) => state.appointments.daySlotsGrouped;
export const map = (state) => state.appointments.map;
export const isWidgetsVisibleState = (state) => state.appointments.showWidgets;
export const isBarContextState = (state) => state.appointments.isBarsContext;
export const widgetCountsState = (state) => state.appointments.counts;
export const dayAppointmentsState = (state) => state.appointments.appointments;
export const sessionsState = (state) => state.appointments.sessions;
export const monthStatsState = (state) => state.appointments.monthStats;
export const activeView = (state) => state.appointments.view;
export const selectedDate = (state) => state.appointments.currentDay;
export const selectedFilters = (state) => state.appointments.filters;
export const practitionerRolesDict = (state) => state.appointments.practitionerRoles;
export const appointmentBooksState = (state) => state.appointments.appointmentBooksState;

export const activeContext = (state) => state.appointments.context;
export const listSortState = (state) => state.appointments.listSort;
export const filterLocationsState = (state) => state.appointments.filterLocations;
export const filterClinicNamesState = (state) => state.appointments.filterClinicNames;
export const filterSessionNamesState = (state) => state.appointments.filterSessionNames;
export const filterSpecialtiesState = (state) => state.appointments.filterSpecialties;
export const filterCliniciansState = (state) => state.appointments.filterClinicians;
export const filterPractitionerRoles = (state) => state.appointments.filterPractitionerRoles;
export const filterAutoLocationSelect = (state) => state.appointments.autoLocationSelect;

export const selectedPatient = (state) =>
    state.appointments.modalHelperState?.patient;
export const swimLanesViewOpen = (state) => state.appointments.context === CONTEXT_OPTIONS.SWIM_LANES;
export const calendarViewOpen = (state) => state.appointments.context === CONTEXT_OPTIONS.CALENDAR;
export const listViewOpen = (state) => state.appointments.context === CONTEXT_OPTIONS.LIST;
// Modals
export const isAssignSessionModalOpen = (state) =>
    state.appointments.modalsOpen?.[APPOINTMENT_MODALS.ASSIGN_SESSION];
export const isAddNewEdSessionModalOpen = (state) =>
    state.appointments.modalsOpen?.[APPOINTMENT_MODALS.ADD_EDIT_SESSION];
export const isChangeReviewStatusModalOpen = (state) =>
    state.appointments.modalsOpen?.[APPOINTMENT_MODALS.CHANGE_REVIEW_STATUS];
export const isPatientRegistryModalOpen = (state) =>
    state.appointments.modalsOpen?.[APPOINTMENT_MODALS.PATIENT_REGISTRY];
export const isStatusTimelineModalOpen = (state) =>
    state.appointments.modalsOpen?.[APPOINTMENT_MODALS.STATUS_TIMELINE];
export const appointmentsModalHelperState = (state) =>
    state.appointments.modalHelperState;
export const dayCalendarShowAdditionalTiles = (state) =>
    state.appointments.dayCalendarShowAdditionalTiles;
export const dayCalendarHelperState = (state) =>
    state.appointments.dayCalendarHelperState;
export const schedulerFacility = (state) => state.appointments.facility;

export const {
    setPatients,
    setMap,
    setSelectedDate,
    setView,
    setContext,
    setModalOpen,
    setDayViewOpen,
    setModalHelperState,
    setDayCalendarShowAdditionalTiles,
    setDayCalendarHelperState,
    setShowWidgets,
    setSessions,
    setSlots,
    setDaySlots,
    setDaySlotsGrouped,
    setHiddenGroups,
    setDaySlotsUnfiltered,
    setAppliedFilters,
    setFilters,
    setFilterLocations,
    setFilterClinicNames,
    setFilterSessionNames,
    setFilterSpecialties,
    setFilterClinicians,
    setCounts,
    setAppointments,
    setMonthStats,
    setSessionsCache,
    setListSort,
    setBarsContext,
    setContextCache,
    setPractitionerRoles,
    setFilterPractitionerRoles,
    setFacility,
    setAutoLocationSelect,
    setAppointmentBooksState,
} = appointmentSlice.actions;

export const openAppointmentModal =
    (modal, helperState = null) =>
        (dispatch, getState) => {
            const currentStateClone = _.cloneDeep(getState().appointments.modalsOpen);
            currentStateClone[modal] = true;

            if (helperState) {
                dispatch(setModalHelperState(helperState));
            }

            dispatch(setModalOpen({ ...currentStateClone }));
        };

export const closeAppointmentModal = (modal) => (dispatch) => {
    dispatch(setModalOpen({ [modal]: false }));
    dispatch(setModalHelperState(null));
};

export const changeContext = (context) => (dispatch, getState) => {
    dispatch(setContext(context));
    const a = getState().appointments;
    dispatch(updateFiltersAvailability(context, a.filterLocations,
        a.filterClinicNames, a.filterSessionNames, a.filterSpecialties,
        a.filterClinicians, a.filterPractitionerRoles, a.currentDay));
};

export const toggleBarsContext = () => (dispatch, getState) => {
    const currentContext = getState().appointments.isBarsContext;
    const state = getState().appointments;
    const newCache = {
        isBarsContext: currentContext,
        sessions: state.sessions,
        sessionsCache: state.sessionsCache,
        appointments: state.appointments,
        appliedFilters: state.appliedFilters,
        filters: state.filters,
        daySlotsUnfiltered: state.daySlotsUnfiltered,
        daySlots: state.daySlots,
        daySlotsGrouped: state.daySlotsGrouped,
        hiddenGroups: state.hiddenGroups,
        monthStats: state.monthStats,
        filterLocations: state.filterLocations,
        filterClinicNames: state.filterClinicNames,
        filterSessionNames: state.filterSessionNames,
        filterSpecialties: state.filterSpecialties,
        filterClinicians: state.filterClinicians,
        filterPractitionerRoles: state.filterPractitionerRoles,
        counts: state.counts,
        listSort: state.listSort,
    }
    if (state.contextCache.isBarsContext === (!currentContext)) {
        dispatch(setSessions(state.contextCache.sessions));
        dispatch(setSessionsCache(state.contextCache.sessionsCache));
        dispatch(setAppointments(state.contextCache.appointments));
        dispatch(setAppliedFilters(state.contextCache.appliedFilters));
        dispatch(setFilters(state.contextCache.filters));
        dispatch(setDaySlotsUnfiltered(state.contextCache.daySlotsUnfiltered));
        dispatch(setDaySlots(state.contextCache.daySlots));
        dispatch(setDaySlotsGrouped(state.contextCache.daySlotsGrouped));
        dispatch(setHiddenGroups(state.contextCache.hiddenGroups));
        dispatch(setMonthStats(state.contextCache.monthStats));
        dispatch(setFilterLocations(state.contextCache.filterLocations));
        dispatch(setFilterClinicNames(state.contextCache.filterClinicNames));
        dispatch(setFilterSessionNames(state.contextCache.filterSessionNames));
        dispatch(setFilterSpecialties(state.contextCache.filterSpecialties));
        dispatch(setFilterClinicians(state.contextCache.filterClinicians));
        dispatch(setFilterPractitionerRoles(state.contextCache.filterPractitionerRoles));
        dispatch(setCounts(state.contextCache.counts));
        dispatch(setListSort(state.contextCache.listSort));
    } else {
        dispatch(setSessions([]));
        dispatch(setSessionsCache({}));
        dispatch(setAppointments([]));
        dispatch(setAppliedFilters(INIT_FILTER_STATE));
        dispatch(setFilters(INIT_FILTER_STATE));
        dispatch(setDaySlotsUnfiltered([]));
        dispatch(setDaySlots([]));
        dispatch(setDaySlotsGrouped([]));
        dispatch(setHiddenGroups({}));
        dispatch(setMonthStats({}));
        dispatch(setFilterLocations([]));
        dispatch(setFilterClinicNames([]));
        dispatch(setFilterSessionNames([]));
        dispatch(setFilterSpecialties([]));
        dispatch(setFilterClinicians([]));
        dispatch(setFilterPractitionerRoles([]));
        dispatch(setCounts(INIT_COUNTS_STATES));
        dispatch(setListSort(null));
    }
    dispatch(setContextCache(newCache));
    dispatch(setBarsContext(!currentContext));

};

export const resetAppointments = () => (dispatch) => {
    dispatch(setSessions([]));
    dispatch(setSessionsCache({}));
    dispatch(setAppointments([]));
    dispatch(setAppliedFilters(INIT_FILTER_STATE));
    dispatch(setFilters(INIT_FILTER_STATE));
    dispatch(setDaySlotsUnfiltered([]));
    dispatch(setDaySlots([]));
    dispatch(setDaySlotsGrouped([]));
    dispatch(setHiddenGroups({}));
    dispatch(setMonthStats({}));
    dispatch(setFilterLocations([]));
    dispatch(setFilterClinicNames([]));
    dispatch(setFilterSessionNames([]));
    dispatch(setFilterSpecialties([]));
    dispatch(setFilterClinicians([]));
    dispatch(setFilterPractitionerRoles([]));
    dispatch(setCounts(INIT_COUNTS_STATES));
    dispatch(setListSort(null));
}

export const addPatientSchedulerNote = ({ ptId, newValues, oldValues, patientsInWardsRef, isDeleted, noteDate, isRescheduled, timeUpdate, onAddNote, dnaObj }) => async (dispatch, getState) => {
    let note = `## *Appointment ${isRescheduled ? "Reschedule" : "Schedule"}*\n\n`;
    let session = _.find(getState().appointments.sessions, s => s.sessionId === newValues?.assignedSession);

    if (!session) {
        session = await API.getAppointmentSession(newValues?.assignedSession);
    }

    try {
        let snackbarMsg = "";
        const oldStatus = _.find(SLOT_STATUSES, s => s.value === oldValues?.status);
        const newStatus = _.find(SLOT_STATUSES, s => s.value === newValues?.status);
        const appointmentDateString = moment(oldValues?.startDate).format(
            // "Do MMM h:mma",
            window.envConfig.defaultDateTimeFormat
        );

        if (!!isDeleted) {
            note += `* Appointment on ${appointmentDateString} has been cancelled\n`;
            snackbarMsg = "Appointment's cancellation note successfully saved. "
        } else {
            if ((!oldValues && !!newValues) || (isRescheduled && oldValues && newValues && oldValues?.assignedSession !== newValues?.assignedSession)) {
                note += `* **Appointment date**: ${moment(newValues?.startDate).format(window.envConfig.defaultDateTimeFormat)}\n`;
                note += `* **Appointment duration**: ${moment(newValues.endDate).diff(moment(newValues.startDate), 'minute')} minutes\n`;

                if (newValues?.description) note += `* **Appointment reason**: ${newValues.description}\n`;
                if (session?.sessionName) note += `* **Session name**: ${session?.sessionName}\n`;
                if (session?.hospital?.description) note += `* **Hospital**: ${session?.hospital?.description}\n`;
                if (session?.mapWard?.name) note += `* **Ward name**: ${session?.mapWard?.name}\n`;
                if (session?.clinic?.description) note += `* **Clinic**: ${session?.clinic?.description}\n`;
                if (session?.specialty?.__description) note += `* **Specialty**: ${session?.specialty?.__description}\n`;

                snackbarMsg = "New scheduled appointment note successfully saved."
            } else if (newValues && oldValues) {
                const date = noteDate ? moment(noteDate) : moment();

                if (session?.sessionName) note += `* **Session name**: ${session?.sessionName}\n`;
                note += `* **Appointment date**: ${moment(newValues?.startDate).format(window.envConfig.defaultDateTimeFormat)}\n`;
                note += `* **Appointment duration**: ${moment(newValues.endDate).diff(moment(newValues.startDate), 'minute')} minutes\n`;
                newValues?.description && (note += `* **Appointment reason**: ${newValues?.description}\n`);

                // if (!isRescheduled || (oldStatus?.name !== newStatus?.name)) note += `Appointment status updated ${oldStatus?.name !==}from ${oldStatus?.name} to ${newStatus?.name} on ${moment(date).format(window.envConfig.defaultDateTimeFormat)}`;
                if (isRescheduled || (oldStatus?.name !== newStatus?.name) || timeUpdate) note += `\nAppointment status updated${timeUpdate || (oldStatus?.name !== newStatus?.name) ? ` from ${oldStatus?.name} to ${newStatus?.name}` : ''} on ${moment(date).format(window.envConfig.defaultDateTimeFormat)}\n`;

                // DNA REASON
                if ([SLOT_STATUSES.WNB.name, SLOT_STATUSES.DNA.name].includes(newStatus?.name)) {
                    if (dnaObj?.dnaReason) {
                        note += `* **${newStatus.name} status reason**: ${dnaObj?.dnaReason}\n`;
                    }
                    if (dnaObj?.dnaFeedback) {
                        note += `* **${newStatus.name} comment**: ${dnaObj?.dnaFeedback}\n`;
                    }
                }

                snackbarMsg = "New update appointment note successfully saved.";
            }
        }

        let noteObj = {
            note,
            patientsInWardsRef: [],
            noteType: 'Appointments',
            referenceId: newValues?.__id || oldValues?.__id || "",
        }

        if (patientsInWardsRef) noteObj.patientsInWardsRef = _.isArray(patientsInWardsRef) && !_.isEmpty(patientsInWardsRef) ?
            patientsInWardsRef : _.isString(patientsInWardsRef) ? [patientsInWardsRef] : [];

        const response = await API.addClinicalNote(ptId, noteObj);

        if (response) {
            dispatch(setSnackbar({ type: 'success', message: snackbarMsg || 'New appointment note saved.' }));
            if (onAddNote && typeof onAddNote === 'function') onAddNote()
        }
    } catch (e) {
        console.error(e);
        dispatch(setSnackbar({ type: 'error', message: 'New appointment note not saved.' }));
    }
}

export const changeFilterValue = (value, filterKey) => (dispatch, getState) => {
    const newFilter = {
        ...getState().appointments.filters,
        [filterKey]: value,
    };

    dispatch(setFilters(newFilter));

    dispatch(filterAppointmentSlots());
};

export const changeSelectedDate = (date) => (dispatch, getState) => {
    if (!getState().appointments.currentDay.isSame(date)) {
        if (getState().appointments.filters.startDate.isAfter(date) || getState().appointments.filters.endDate.isBefore(date)) {
            let startDate = date.clone().startOf('month');
            let endDate = date.clone().endOf('month');
            dispatch(setFilters({
                ...getState().appointments.filters,
                startDate: startDate,
                endDate: endDate,
            }));
            dispatch(setSelectedDate(date));
        } else {
            dispatch(setSelectedDate(date));
        }
    }
};

export const selectPatientForAppointment =
    (patient, fetchPatientInfo = false) =>
        async (dispatch, getState) => {
            if (!patient?.__id) {
                return dispatch(
                    setSnackbar({
                        type: 'error',
                        message:
                            'Patient is not valid. Please try again later or contact support.',
                    }),
                );
            }

            try {
                let pt = patient;

                dispatch(setLoading(true));

                if (!!fetchPatientInfo) {
                    const apiResponse = await API.getPatientAttendancesHistory(pt?.__id);

                    if (!_.isEmpty(apiResponse)) {
                        pt = apiResponse?.[0];
                    } else {
                        dispatch(
                            setSnackbar({
                                type: 'error',
                                message:
                                    'Cannot get patient history. Please admit patient first.',
                            }),
                        );
                    }
                }

                const currentModalHelperState = getState().appointments.modalHelperState;

                dispatch(
                    setModalHelperState({
                        ...currentModalHelperState,
                        selectedPatient: pt,
                    }),
                );
            } catch (e) {
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: 'Error selecting patient. Please try again later.',
                    }),
                );
            } finally {
                dispatch(setLoading(false));
            }
        };

//////////////////////////
// APPOINTMENT SESSIONS //
//////////////////////////

export const createAppointmentSession =
    (session, onSuccess) => async (dispatch, getState) => {
        dispatch(setAgyleHeaderLoading(true));
        const newSession = createAppointmentSessionObject(session);

        try {
            const apiResponse = await API.createAppointmentSession(newSession);

            if (apiResponse) {
                if (onSuccess) {
                    onSuccess(apiResponse);
                }

                dispatch(getAppointmentSlots(true));
            }

            return apiResponse?.__data || [];
        } catch (e) {
            dispatch(showExceptionSnackbar(e));
        } finally {
            dispatch(getAppointmentSlots());
            dispatch(setAgyleHeaderLoading(false));
        }
    };

export const bulkCreateAppointmentSessions =
    (sessionObj, onSuccess) => async (dispatch, getState) => {
        dispatch(setAgyleHeaderLoading(true));
        const newSessions = bulkCreateAppointmentSessionObjects(sessionObj);
        try {
            const apiResponse = await API.bulkCreateAppointmentSessions({ sessionList: newSessions });

            if (apiResponse) {
                onSuccess(apiResponse);

                dispatch(getAppointmentSlots(true));
            }

            return apiResponse?.__data || [];
        } catch (e) {
            dispatch(showExceptionSnackbar(e));
        } finally {
            dispatch(getAppointmentSlots());
            dispatch(setAgyleHeaderLoading(false));
        }
    };

export const updateAppointmentSession =
    (sessionId, updated, onSuccess) => async (dispatch, getState) => {
        dispatch(setAgyleHeaderLoading(true));
        try {
            const apiResponse = await API.updateAppointmentSession(
                sessionId,
                updated,
            );

            if (apiResponse) {
                dispatch(
                    openSnackbar('success', 'Appointment session has been successfully updated.'),
                );
                if (onSuccess) {
                    onSuccess();
                }

                dispatch(getAppointmentSlots(true));
            }

            return apiResponse?.__data || [];
        } catch (e) {
            dispatch(showExceptionSnackbar(e));
        } finally {
            dispatch(getAppointmentSlots());
            dispatch(setAgyleHeaderLoading(false));
        }
    };

export const deleteAppointmentSession =
    (sessionId, onSuccess) => async (dispatch, getState) => {
        if (!sessionId) return dispatch(
            openSnackbar('error', 'Please provide valid session.'),
        );

        dispatch(setAgyleHeaderLoading(true));
        try {
            const apiResponse = await API.deleteAppointmentSession(sessionId);

            if (apiResponse) {
                dispatch(openSnackbar('success', 'Appointment session has been successfully closed.'));
                if (onSuccess) onSuccess();

                dispatch(getAppointmentSlots(true));
            }

            return apiResponse?.__data || [];
        } catch (e) {
            console.log("ERR:", e);
            dispatch(showExceptionSnackbar(e));
        } finally {
            dispatch(getAppointmentSlots());
            dispatch(setAgyleHeaderLoading(false));
        }
    };

export const showExceptionSnackbar = (e) => (dispatch) => {
    const message = String(e.response?.data || e.message).split('Exception: ')?.[1];
    const msg = message.split("\n")[0];

    dispatch(
        setSnackbar({
            type: 'error',
            message: msg,
        }),
    );
};


const getSessions = (month, forceRefresh = false, hospitalId) => async (dispatch, getState) => {
    const cache = getState().appointments.sessionsCache;
    const monthStart = month.clone().startOf('month');
    const monthEnd = monthStart.clone().add(1, 'month');
    const monthStr = month.format('YYYY_MM');

    // Helper to compute result from sessions list
    const buildResult = (sessionsList) => {
        const selectedDayIso = (getState().appointments?.currentDay && getState().appointments.context !== CONTEXT_OPTIONS.CALENDAR)
            ? getState().appointments.currentDay.format('YYYY-MM-DD')
            : null;
        
        const apiPractitionerRoles = getState().dicts.clinicianRoleDict;
        if (_.isArray(apiPractitionerRoles)) dispatch(setPractitionerRoles(apiPractitionerRoles));

        return {
            sessions: sessionsList,
            cached: moment().valueOf(),
            sessionDicts: getSessionDicts(sessionsList, apiPractitionerRoles, getState().dicts.clinicians, selectedDayIso, getState().appointments.filters.clinicians),
            facility: hospitalId,
        };
    };

    if (cache[monthStr] && cache[monthStr].cached > (moment().valueOf() - 600000) && !forceRefresh && cache[monthStr]?.facility === hospitalId) {
        // Cache Hit: Recompute dicts to ensure selectedDay and activeFilters are up to date
        return buildResult(cache[monthStr].sessions);
    } else {
        // Cache Miss: Fetch and build
        const apiSessions = await API.getSessionsByDateRange(monthStart, monthEnd, getState().appointments.isBarsContext, hospitalId);
        
        let sessionres = [];
        _.forEach(apiSessions || [], (sess) => {
            sess.monthDate = monthStart.clone();
            getSlotsForSession(sess, monthStart, monthEnd);
            sessionres.push(sess);
        });

        const result = buildResult(sessionres);
        
        // Update Cache
        const MAX_CACHED_MONTHS = 2;
        const newCache = { ...cache };
        const cacheKeys = Object.keys(newCache).sort();

        while (cacheKeys.length >= MAX_CACHED_MONTHS) {
            const oldestKey = cacheKeys.shift();
            if (oldestKey !== monthStr) {
                delete newCache[oldestKey];
            }
        }

        newCache[monthStr] = result;
        dispatch(setSessionsCache(newCache));
        return result;
    }
};

const groupFilters = (sessions, keyPath, getId, getDescription, getValue, fallback) => {
    return _(sessions)
        .groupBy(sess => {
            const val = _.get(sess, keyPath);
            return val ? getId(val) : 'noVal';
        })
        .map((grouped, id) => {
            const first = grouped[0];
            const val = _.get(first, keyPath);
            const isFallback = id === 'noVal';

            return {
                __id: isFallback ? fallback.__id : getId(val),
                __description: isFallback ? fallback.__description : getDescription(val),
                value: isFallback ? fallback.value : getValue(val),
                disabled: false,
                sess: grouped
            };
        })
        .value();
};


const getSessionDicts = (sessions, practitionerRoles, clinicianDict, selectedDay, activeFilters) => {
    let result = { locations: [], clinics: [], uniqueSessions: [], specialties: [], clinicians: [], practitionerRoles: [] };
    const uniqueSessionsMapped = _.map(sessions, sess => {
        return { __id: sess?.groupId, __description: sess?.sessionName, value: sess?.groupId, sess: sess, disabled: false };
    });
    result.uniqueSessions = _.uniqBy(uniqueSessionsMapped, '__id');

    result.locations = groupFilters(
        sessions,
        'mapWard',
        ward => ward?.wardsId?.[0],
        ward => ward?.name,
        ward => ward?.wardsId?.[0],
        { __id: 'noVal', __description: 'Emergency Department', value: 'ED' }, // Fallback for old ED sessions location
    );

    result.clinics = groupFilters(
        sessions,
        'clinic',
        clinic => clinic?.__id,
        clinic => clinic?.description || clinic?.__description,
        clinic => clinic?.__id,
        { __id: 'noVal', __description: 'Not assigned', value: '' }
    );

    result.specialties = groupFilters(
        sessions,
        'specialty',
        spec => spec?.__id,
        spec => spec?.__description,
        spec => spec?.__id,
        { __id: 'noVal', __description: 'Not assigned', value: null }
    );

    // result.clinicians = groupFilters(
    //     sessions,
    //     'consultant',
    //     consultant => consultant?.__id,
    //     consultant => getConsultantName(consultant),
    //     consultant => consultant?.__id,
    //     { __id: 'noVal', __description: 'Not assigned', value: null }
    // );
    result.clinicians = groupClinicianFilter(
        sessions,
        'consultant',
        consultant => consultant?.__id,
        consultant => getConsultantName(consultant),
        consultant => consultant?.__id,
        { __id: 'noVal', __description: 'Not assigned', value: null },
        clinicianDict,
        selectedDay,
        activeFilters
    );

    result.practitionerRoles = groupFilters(
        sessions,
        'practitionerRole',
        id => id,
        id => {
            const found = _.find(practitionerRoles, { __id: id });
            return found?.__description || 'Not assigned';
        },
        id => id,
        { __id: 'noVal', __description: 'Not assigned', value: null }
    );

    return result;
};

const updateFiltersAvailability = (context, filterLocations, filterClinicNames, filterSessionNames, filterSpecialties, filterClinicians, filterPractitionerRoles, currDay) => (dispatch, getState) => {
    const sday = currDay.format('YYYY_MM_DD');
    const filters = getState().appointments.filters;

    // Calculate disabled value without mutating original object
    // Uses shallow copy to preserve sess reference (avoids deep copying heavy daySlots data)
    const getDisabledValue = (f, activeOptions) => {
        // If filter is currently selected, force enabled so it stays visible
        const filterId = f.value ? f.value : f.__id;
        // activeOptions is array of {__id, value, ...}
        if (activeOptions && activeOptions.some(opt => (opt.value ? opt.value : opt.__id) === filterId)) {
            return false;
        }

        if (context === CONTEXT_OPTIONS.CALENDAR) {
            return false;
        }
        if (_.isArray(f.sess)) {
            const anyEnabled = f.sess.some(sessItem =>
                sessItem?.daySlots?.[sday] && !_.isEmpty(sessItem.daySlots[sday])
            );
            return !anyEnabled;
        } else {
            return !(f?.sess?.daySlots?.[sday] && !_.isEmpty(f?.sess?.daySlots?.[sday]));
        }
    };

    // Shallow copy with only disabled flag changed - preserves sess reference
    const shallowCopyWithDisabled = (arr, activeOptions) => arr.map(f => ({ ...f, disabled: getDisabledValue(f, activeOptions) }));

    dispatch(setFilterLocations(shallowCopyWithDisabled(filterLocations, filters.locations)));
    dispatch(setFilterClinicNames(shallowCopyWithDisabled(filterClinicNames, filters.clinics)));
    dispatch(setFilterSessionNames(shallowCopyWithDisabled(filterSessionNames, filters.sessions)));
    dispatch(setFilterSpecialties(shallowCopyWithDisabled(filterSpecialties, filters.specialties)));
    dispatch(setFilterClinicians(shallowCopyWithDisabled(filterClinicians, filters.clinicians)));
    dispatch(setFilterPractitionerRoles(shallowCopyWithDisabled(filterPractitionerRoles, filters.practitionerRoles)));
}

const updateSlots = (appointments, patients, startDay) => {
    appointments.sessionshash = {};
    // appointments.counts = {total: patients.length};
    const sessionsForDayCount = (getSessionForSelecteDay(appointments.sessions, startDay) || []).length;
    appointments.counts = { total: _.countBy(patients, pt => pt.status !== PROHIBITED_SLOT_STATUSES.LOCKED.value && pt.status !== PROHIBITED_SLOT_STATUSES.AVAILABLE.value).true || 0 };
    appointments.counts.beeingSeen = _.countBy(patients, pt => pt.status === SLOT_STATUSES.BEEING_SEEN.value).true || 0;
    appointments.counts.notRegistered = _.countBy(patients, pt => pt.status === SLOT_STATUSES.NOT_REGISTERED.value).true || 0;
    appointments.counts.arrived = _.countBy(patients, pt => pt.status === SLOT_STATUSES.ARRIVED.value).true || 0;
    appointments.counts.doNotAttend = _.countBy(patients, pt => pt.status === SLOT_STATUSES.DNA.value || pt.status === SLOT_STATUSES.WNB.value).true || 0;
    appointments.counts.departed = _.countBy(patients, pt => pt.status === SLOT_STATUSES.DEPARTED.value).true || 0;
    appointments.counts.notLocked = _.countBy(patients, pt => pt.status !== PROHIBITED_SLOT_STATUSES.LOCKED.value).true || 0;
    appointments.counts.sessions = sessionsForDayCount || 0;

    // let groupedBySess = _.groupBy(patients, 'assignedSession');
    _.forEach(patients, pts => {
        pts.startDate = moment(pts.startDate);
    });
    appointments.counts.nextBeeingSeen = _.countBy(patients, pt => {
        return pt.status === SLOT_STATUSES.ARRIVED.value ||
            (pt.status === SLOT_STATUSES.NOT_REGISTERED.value && pt.startDate.isAfter(moment()));
    }).true || 0;
    let slots = [];
    let dayField = startDay.format('YYYY_MM_DD');
    _.forEach(appointments.sessions, (sess) => {
        if (sess.daySlots[dayField]) {
            slots.push(...sess.daySlots[dayField]);
        }
    });
    slots = _.unionWith(patients, slots, (arrVal, othVal) => {
        return arrVal.startDate.isSame(othVal.startDate) && arrVal.assignedSession === othVal.assignedSession;
    });
    slots = _.sortBy(slots, obj => {
        return obj.startDate.valueOf();
    });
    appointments.daySlotsUnfiltered = slots;
};

export const filterSessions = (filter, allsessions, date, monthView = false) => {
    let fsessions = _.map(allsessions, session => {
        return session;
    });
    if (filter.locations && filter.locations.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.locations, l => {
                // return l.__id === s.wardId;
                return (l.__id !== 'noVal' ? l.__id : l.value) === s.wardId;
            });
        });
    }
    if (filter.clinics && filter.clinics.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.clinics, l => {
                return (l.value ? l.__id : l.value) === s.clinicsId;
                // return l.__id === s.clinicsId;
            });
        });
    }
    if (filter.sessions && filter.sessions.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.sessions, l => {
                return l.__id === s.groupId;
            });
        });
    }
    if (filter.specialties && filter.specialties.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.specialties, l => {
                return (l.value ? l.__id : l.value) === s.assignedSpecialty;
            });
        });
    }
    if (filter.clinicians && filter.clinicians.length > 0) {
        fsessions = _.filter(fsessions, s => {
            const { effectiveClinician } = s;
            const overriddenCliniciansEntries = effectiveClinician && monthView ? Object.entries(effectiveClinician) : null;

            const overriddenForMonth = overriddenCliniciansEntries ? overriddenCliniciansEntries.filter(([overriddenDate, clinicianId]) => {
                if (moment(date, "YYYY-MM-DD").isSame(moment(overriddenDate, "YYYY-MM-DD"), 'month')) {
                    return clinicianId;
                }
            }).map(([_a,b]) => b) : null;

            return _.find(filter.clinicians, l => {
                let val = s.practitioner;
                const clinician = l.value ? l.__id : l.value;

                if (monthView) {
                    if (_.isArray(overriddenForMonth)) {
                        return overriddenForMonth.includes(clinician) || clinician === val;
                    }
                } else {
                    if (effectiveClinician && effectiveClinician[date]) {
                        val = effectiveClinician[date];
                    }

                    return clinician === val;
                }
            });
        });
    }
    if (filter.practitionerRoles && filter.practitionerRoles.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.practitionerRoles, l => {
                return (l.value ? l.__id : l.value) === s.practitionerRole;
            });
        });
    }
    return fsessions;
};

export const hideShowGroup = (group) => async (dispatch, getState) => {
    if (getState().appointments.hiddenGroups[group]) {
        let newHG = { ...getState().appointments.hiddenGroups };
        delete newHG[group];
        await dispatch(setHiddenGroups(newHG));
    } else {
        const newHG = { ...getState().appointments.hiddenGroups, [group]: true };
        await dispatch(setHiddenGroups(newHG));
    }
    dispatch(filterAppointmentSlots())
}

const calculateGrouping = (filter, filtered, fsessions, hiddenGroups, sort) => {
    if (filter && (filter.clinics?.length > 1 ||
        filter.sessions?.length > 1 ||
        filter.specialties?.length > 1 ||
        filter.clinicians?.length > 1 ||
        filter.practitionerRoles?.length > 1
    )) {
        let group = filter.clinics?.length > 1 ? { v: 'clinicsId', f: filter.clinics } :
            filter.sessions?.length > 1 ? { v: 'groupId', f: filter.sessions } :
                filter.specialties?.length > 1 ? { v: 'assignedSpecialty', f: filter.specialties } :
                    filter.practitionerRoles?.length > 1 ? { v: 'practitionerRole', f: filter.practitionerRoles } :
                        { v: 'practitioner', f: filter.clinicians };
        group.ff = _.reduce(group.f, (obj, param) => {
            obj[param.__id] = param
            return obj;
        }, {});
        const groupedSessions = {};
        _.forEach(fsessions, sess => {
            groupedSessions[sess.sessionId] = sess;
        });
        let grouped = _.groupBy(filtered, app => groupedSessions[app.assignedSession][group.v]);
        let result = [];
        for (var groupId in grouped) {
            var appList = grouped[groupId];
            if (appList && appList.length > 0) {
                const groupSession = groupedSessions[appList[0].assignedSession];
                result.push({
                    assignedSession: groupSession.sessionId,
                    groupName: group?.ff?.[groupSession?.[group?.v]]?.__description || 'Not group name',
                    groupId: groupSession[group.v],
                    count: appList.length,
                    appointments: appList,
                    hidden: hiddenGroups[groupSession[group.v]]
                });
                if (!hiddenGroups[groupSession[group.v]]) {
                    if (sort) {
                        result.push(..._.orderBy(appList, sort.value, sort.direction));
                    } else {
                        result.push(...appList);
                    }
                }
            }
        }
        return result;
    }
    if (sort) {
        return _.orderBy(filtered, sort.value, sort.direction);
    }
    return filtered;
};

export const filterAppointmentSlots = () => async (dispatch, getState) => {
    let filter = getState().appointments.filters;
    let filtered = getState().appointments.daySlotsUnfiltered;
    let allsessions = getState().appointments.sessions;
    let hiddenGroups = getState().appointments.hiddenGroups;
    let sorts = getState().appointments.listSort;
    let calendarDate = getState().appointments.currentDay;
    let fsessions = filterSessions(filter, allsessions, calendarDate.format("YYYY-MM-DD"));
    if (filter.view !== VIEW_FILTER_STATES.ALL) {
        let pts = filter.view === VIEW_FILTER_STATES.PTS;
        filtered = _.filter(filtered, app => {
            return pts ? (app.patient !== undefined && app.patient !== null) :
                (app.patient === undefined || app.patient === null);
        });
    }
    let fsessionids = _.map(fsessions, session => {
        return session.sessionId;
    });
    if (fsessionids.length != allsessions.length) {
        filtered = _.filter(filtered, a => {
            return _.includes(fsessionids, a.assignedSession);
        });
    }
    if (filter.statuses && filter.statuses.length > 0) {
        filtered = _.filter(filtered, a => {
            return _.some(filter.statuses, s => {
                if (s.__id === SLOT_STATUSES.DNA.value || s.__id === SLOT_STATUSES.WNB.value) {
                    return a.status === SLOT_STATUSES.DNA.value || a.status === SLOT_STATUSES.WNB.value;
                } else {
                    return s.__id === a.status;
                }
            });
        });
    }
    dispatch(calculateMonthStats(filter.startDate.clone(), allsessions));
    dispatch(setDaySlots(filtered));
    dispatch(setDaySlotsGrouped(calculateGrouping(filter, filtered, fsessions, hiddenGroups, sorts)));
};

export const filterStats = (stat, filters, calendarDate, monthView = false) => {
    if (!stat || !stat.monthStart) return {};
    let fsessions = filterSessions(filters, stat.sessions, calendarDate.format("YYYY-MM-DD"), monthView);
    let monthStats = {};
    let monthStart = stat.monthStart.clone();
    let monthEnd = stat.monthEnd;
    const filteredWithClinician = [];

    while (monthStart.isBefore(monthEnd) || monthStart.isSame(monthEnd)) {
        let strDate = monthStart.format('YYYY_MM_DD');
        let numDay = monthStart.date();
        monthStats[numDay] = { allslots: 0, takenslots: 0, outsideSessionSlots: 0 };
        _.forEach(fsessions, s => {
            const { effectiveClinician } = s;
            let includeSession = false;
            if (filters.clinicians && filters.clinicians.length > 0) {
                let val = s.practitioner;
                const clinicianIds = filters.clinicians.map(l => l.value ? l.__id : l.value);

                if (monthView) {
                    const date = monthStart.format("YYYY-MM-DD");
                    if (effectiveClinician && effectiveClinician[date]) {
                        includeSession = clinicianIds.includes(effectiveClinician[date]);
                    } else {
                        includeSession = clinicianIds.includes(val);
                    }
                }
            } else {
                includeSession = true;
            }
            if (includeSession && s.daySlots[strDate]) {
                monthStats[numDay].allslots += s.daySlots[strDate].length;
                filteredWithClinician.push(s);
            }
        });
        monthStart = monthStart.add(1, 'day');
    }

    let filteredStatList = _.filter(stat.statList, s => {
        return _.findIndex(filteredWithClinician, fs => {
            if (filters.clinicians && filters.clinicians.length > 0) {
                let val = fs.practitioner;
                const clinicianIds = filters.clinicians.map(l => l.value ? l.__id : l.value);
                const { effectiveClinician } = fs;
                const slotDate = moment(s?.appDay).format("YYYY-MM-DD")

                if (effectiveClinician?.[slotDate] ? clinicianIds.includes(slotDate) : clinicianIds.includes(val)) {
                    return fs.sessionId == s?.assignedSession;
                }
            } else {
                return fs.sessionId == s?.assignedSession;
            }
        }) > -1;
    });

    _.forEach(filteredStatList, stat => {
        monthStats[moment(stat.appDay).date()].takenslots += stat.count;

        if (_.isNumber(stat?.outsideSessionSlots) && stat?.outsideSessionSlots > 0) {
            monthStats[moment(stat.appDay).date()].allslots += stat.outsideSessionSlots
        }
    });
    return monthStats;
};

export const getSessionForSelecteDay = (sessions, selectedDay) => {
    const day = selectedDay.format('YYYY_MM_DD');
    return _.filter(sessions, session => {
        return session.daySlots[day];
    });
};

export const getSlotWithSession = (slotId) => (dispatch, getState) => {
    const slots = getState().appointments.daySlots;
    const sessions = getState().appointments.sessions;

    const slot = _.find(slots, { __id: slotId });

    if (slot) {
        const session = _.find(sessions, { __id: slot?.assignedSession });

        return ({
            ...slot,
            session
        })
    }

}

export const calculateMonthStats = (slelectedMonth, _allSessions) => async (dispatch, getState) => {
    const hospitalId = getState().appointments.facility;
    const apiStatResponse = await API.getMonthStats(slelectedMonth, [], [], getState().appointments.isBarsContext, hospitalId);
    let monthStats = {};
    let monthStart = slelectedMonth.clone();
    let monthEnd = monthStart.clone().endOf('month');
    const _cachedSessions = await dispatch(getSessions(monthStart, false, hospitalId));
    let allSessions = _cachedSessions.sessions;
    while (monthStart.isBefore(monthEnd) || monthStart.isSame(monthEnd)) {
        let strDate = monthStart.format('YYYY_MM_DD');
        let numDay = monthStart.date();
        monthStats[numDay] = { allslots: 0, takenslots: 0, availableOutsideSlots: 0 };
        _.forEach(allSessions, s => {
            if (s.daySlots[strDate]) {
                monthStats[numDay].allslots += s.daySlots[strDate].length;
            }
        });
        monthStart = monthStart.add(1, 'day');
    }
    _.forEach(apiStatResponse, stat => {
        monthStats[moment(stat.appDay).date()].takenslots += stat.count;

        if (_.isNumber(stat?.outsideSessionSlots) && stat?.outsideSessionSlots > 0) {
            monthStats[moment(stat.appDay).date()].outsideSessionSlots += stat.outsideSessionSlots
        }
    });
    let monthsStats = {
        ...getState().appointments.monthStats,
        [slelectedMonth.format('YYYY_MM')]: {
            monthStats: monthStats,
            cached: moment().valueOf(),
            statList: apiStatResponse,
            sessions: allSessions,
            monthStart: slelectedMonth.clone(),
            monthEnd: monthEnd,
            sessionDicts: _cachedSessions.sessionDicts,
        },
    };
    dispatch(setMonthStats(monthsStats));

};

export const updateMonthStatCached = (slelectedMonth) => async (dispatch, getState) => {
    if (getState().appointments.monthStats[slelectedMonth.format('YYYY_MM')]) {
        if (getState().appointments.monthStats[slelectedMonth.format('YYYY_MM')].cached > (moment().valueOf() - 60000)) {
            return;
        }
    }
    dispatch(calculateMonthStats(slelectedMonth));
};

export const getAppointmentSlots = (forceRefresh = false) => async (dispatch, getState) => {
    dispatch(setAgyleHeaderLoading(true));
    let appointmentsState = getState().appointments;
    const hospId = getState().appointments.facility;

    const cachedSessions = await dispatch(getSessions(appointmentsState.filters.startDate, forceRefresh, hospId));
    dispatch(updateFiltersAvailability(appointmentsState.context, cachedSessions.sessionDicts.locations,
        cachedSessions.sessionDicts.clinics, cachedSessions.sessionDicts.uniqueSessions, cachedSessions.sessionDicts.specialties,
        cachedSessions.sessionDicts.clinicians, cachedSessions.sessionDicts.practitionerRoles, appointmentsState.currentDay));
    // dispatch(setFilterLocations(cachedSessions.sessionDicts.locations));
    // dispatch(setFilterClinicNames(cachedSessions.sessionDicts.clinics));
    // dispatch(setFilterSpecialties(cachedSessions.sessionDicts.specialties));
    // dispatch(setFilterClinicians(cachedSessions.sessionDicts.clinicians));
    // dispatch(setFilterSessionNames(cachedSessions.sessionDicts.uniqueSessions));
    try {
        let endDate = getState().appointments.currentDay.clone().add(1, 'day');
        const apiResponse = await API.getAppointmentSlots(getState().appointments.currentDay, endDate, getState().appointments.isBarsContext, hospId ? `hospitalId=${hospId}` : null);
        let appointments = { sessions: cachedSessions.sessions || [], slots: appointmentsState.slots || [] };
        updateSlots(appointments, apiResponse, appointmentsState.currentDay);
        dispatch(setDaySlotsUnfiltered(appointments.daySlotsUnfiltered));
        dispatch(setCounts(appointments.counts));
        dispatch(setSessions(cachedSessions.sessions));
        dispatch(setAppointments(apiResponse));
        dispatch(setAppliedFilters({
            ...getState().appliedFilters,
            startDate: getState().appointments.filters.startDate,
            endDate: getState().appointments.filters.endDate,
        }));
        dispatch(setHiddenGroups({}));
        dispatch(filterAppointmentSlots());
    } catch (e) {
        console.log('Error getAppointmentSlots', e);
        dispatch(showExceptionSnackbar(e));
        dispatch(openSnackbar('error', e.message));
    } finally {
        dispatch(setAgyleHeaderLoading(false));
    }
};

export const updateAppointmentStatus =
    (slotId, status, onSuccess, onError, noteDate, ptInWardRef, isRescheduled = false, timeUpdate = false, onAddNote) => async (dispatch, getState) => {
        if (!_.isString(slotId)) return false;

        dispatch(setAgyleHeaderLoading(true));
        try {
            const currentSlot = await API.getAppointmentSlot(slotId, false);

            const response = await API.updateAppointmentSlotStatus(slotId, {
                status,
            });

            dispatch(sendUserAudit({ patient: response?.patient }, AUDIT_TYPES.SCHEDULER_CHANGE_STATUS, {
                status,
                appointmentId: slotId,
            }));

            if (onSuccess && response) {
                onSuccess(response);
            }

            const oldVal = currentSlot ? currentSlot : _.find(getState()?.appointments?.appointments, { __id: slotId });

            if (response) {
                let patientsInWardsRef = _.isString(ptInWardRef) ? [ptInWardRef] : [];

                if (oldVal?.patient) {
                    dispatch(addPatientSchedulerNote({
                        ptId: response.patientId,
                        oldValues: oldVal,
                        newValues: response,
                        patientsInWardsRef,
                        noteDate,
                        isDeleted: status === AppointmentSlotStatus.DELETED,
                        isRescheduled,
                        timeUpdate,
                        onAddNote
                    }))
                }

                if (status === SLOT_STATUSES.DNA.value) {
                    const pt = await API.getPatientByID(currentSlot.patientId);
                    if (!pt) return;

                    const tags = pt?.tags || [];
                    const withoutBedas = _.filter(tags, t => t !== PATIENT_TAGS.BEDAS);

                    await API.updatePatientTags(pt.hospitalNumber, withoutBedas).then((e) =>
                        console.log("Flag saved"),
                    );
                }
            }
            dispatch(getAppointmentSlots(true));
            return response;
        } catch (e) {
            if (onError) onError(e);
            dispatch(showExceptionSnackbar(e));
        } finally {
            dispatch(setAgyleHeaderLoading(false));
        }
    };

export const removePatientAppointment = (slotId, onSuccess, onError) => async (dispatch) => {
    dispatch(setAgyleHeaderLoading(true));
    try {
        const response = await API.removeAppointment(slotId);
        if (response && onSuccess) {
            onSuccess(response);
        }
        return response;
    } catch (e) {
        if (onError) onError(e);
        dispatch(showExceptionSnackbar(e));
    } finally {
        dispatch(setAgyleHeaderLoading(false));
        dispatch(getAppointmentSlots());
    }
};


//////////////////////////
//  APPOINTMENT  SLOTS  //
//////////////////////////
export const createAppointmentSlot = (slot, remove, onSuccess, noAddNote = false, attendanceRef) => async (dispatch) => {
    dispatch(setAgyleHeaderLoading(true));
    const preparedSlot = {
        ...slot,
        startDate: moment(slot.startDate).toDate(),
        endDate: moment(slot.endDate).toDate(),
    };
    delete preparedSlot.outsideSessionSlotId;

    try {
        dispatch(sendUserAudit(null, AUDIT_TYPES.SCHEDULER_ASSIGN_SLOT, {
            appointmentSlot: preparedSlot,
            remove,
            attendanceRef,
            outsideSessionSlot: slot?.outsideSessionSlotId,
        }));

        if (remove) {
            const response = await API.removeAppointment(remove);
            if (!response) {
                dispatch(showExceptionSnackbar({ message: 'Cannot remove old appointment' }));
            } else if (!noAddNote) {
                const noteProps = {
                    ptId: apiResponse?.patientId,
                    newValues: response,
                    isDeleted: true,
                }
                if (attendanceRef) noteProps.patientsInWardsRef = attendanceRef;

                dispatch(addPatientSchedulerNote(noteProps));
            }
            return;
        }

        const apiResponse = await API.createAppointmentSlot(preparedSlot, slot?.outsideSessionSlotId);

        if (apiResponse?.__id) {
            onSuccess(apiResponse);
            if (!noAddNote) {
                const noteProps = {
                    ptId: apiResponse?.patientId,
                    newValues: apiResponse,
                    isDeleted: false,
                }
                if (attendanceRef) noteProps.patientsInWardsRef = attendanceRef;

                dispatch(addPatientSchedulerNote(noteProps));
            }
        }
        dispatch(getAppointmentSlots(true)); //just to refresh data
        return apiResponse;
    } catch (e) {
        dispatch(showExceptionSnackbar(e));
    } finally {
        dispatch(setAgyleHeaderLoading(false));
    }
};

export const lockAppointmentSlot = (event, reason, onSuccess) => async (dispatch) => {
    dispatch(setAgyleHeaderLoading(true));

    const preparedLockedSlot = {
        assignedSession: event?.session?.sessionId,
        startDate: moment(event.slot.startDate).toDate(),
        endDate: moment(event.slot.endDate).toDate(),
        description: reason || '',
        wardId: event.session.wardId,
        hospitalId: event.session.hospitalId,
    };

    try {
        dispatch(sendUserAudit(null, AUDIT_TYPES.SCHEDULER_ASSIGN_SLOT, {
            appointmentSlot: event,
            lock: true,
        }));

        const apiResponse = await API.lockAppointmentSlot(preparedLockedSlot);

        if (apiResponse?.__id) onSuccess(apiResponse);
        return apiResponse;
    } catch (e) {
        dispatch(showExceptionSnackbar(e));
    } finally {
        dispatch(setAgyleHeaderLoading(false));
        dispatch(getAppointmentSlots());
    }
};

export const createSingleSlot = (event, onSuccess) => async (dispatch) => {
    dispatch(setAgyleHeaderLoading(true));

    const preparedSingleSlot = {
        assignedSession: event?.session?.sessionId,
        startDate: moment(event.slot.startDate).toISOString(),
        endDate: moment(event.slot.endDate).toISOString(),
        description: '',
        wardId: event.session.wardId,
        hospitalId: event.session.hospitalId,
    };

    try {
        dispatch(sendUserAudit(null, AUDIT_TYPES.SCHEDULER_ASSIGN_SLOT, {
            appointmentSlot: event,
            singleSlot: preparedSingleSlot,
            createSingleSlot: true,
        }));

        const apiResponse = await API.lockAppointmentSlot(preparedSingleSlot, true);

        if (apiResponse?.__id) onSuccess(apiResponse);
        return apiResponse;
    } catch (e) {
        dispatch(showExceptionSnackbar(e));
    } finally {
        dispatch(setAgyleHeaderLoading(false));
        dispatch(getAppointmentSlots());
    }
};

export const reschedulePatient = (destinationEvent, currAppointment) => async (dispatch, getState) => {
    const { session, slot, availableSlotOutsideSession } = destinationEvent;

    const newAppData = {};
    let bothOutsideSessionSlots = false;
    let currentAppointmentSlotOutsideSession = false;

    if (slot.startDate && slot.endDate) {
        newAppData.startDate = moment(slot.startDate).toDate();
        newAppData.endDate = moment(slot.endDate).toDate();
    }
    if (currAppointment?.wardId !== session.wardId) newAppData.wardId = session.wardId;
    if (currAppointment?.hospitalId !== session.hospitalId) newAppData.hospitalId = session.hospitalId;
    if (currAppointment?.assignedSession !== session.sessionId) newAppData.assignedSession = session.sessionId;

    if (moment(slot?.startDate).isSame(moment(currAppointment?.startDate), 'day') && currAppointment?.session) {
        const currSlotStart = moment(currAppointment?.startDate);
        const currSlotEnd = moment(currAppointment?.endDate);

        const sessionStart = currSlotStart.clone().set({
            hour: moment(currAppointment?.session?.startDate).hours(),
            minute: moment(currAppointment?.session?.startDate).minute(),
            second: 0,
            millisecond: 0,
        });
        const sessionEnd = currSlotStart.clone().set({
            hour: moment(currAppointment?.session?.endDate).hours(),
            minute: moment(currAppointment?.session?.endDate).minute(),
            second: 0,
            millisecond: 0,
        });

        currentAppointmentSlotOutsideSession = (
            currSlotStart.isSameOrBefore(sessionStart, 'minute') &&
            currSlotEnd.isSameOrBefore(sessionStart, 'minute')
        ) || (
                currSlotStart.isSameOrAfter(sessionEnd, 'minute') &&
                currSlotEnd.isSameOrAfter(sessionEnd, 'minute')
            );

        bothOutsideSessionSlots = currentAppointmentSlotOutsideSession && availableSlotOutsideSession &&
            slot?.status === PROHIBITED_SLOT_STATUSES.AVAILABLE.value || false;
    }

    dispatch(setAgyleHeaderLoading(true));

    try {
        dispatch(sendUserAudit(null, AUDIT_TYPES.SCHEDULER_RESCHEDULE_SLOT, {
            oldAppointment: currAppointment,
            changedAppointmentData: newAppData,
            event: destinationEvent,
        }));

        // const updatedAppointment = await API.updateAppointmentSlot(currAppointment?.__id, newAppData, bothOutsideSessionSlots && slot?.__id, currentAppointmentSlotOutsideSession && !availableSlotOutsideSession);
        const updatedAppointment = await API.updateAppointmentSlot(currAppointment?.__id, newAppData, bothOutsideSessionSlots && slot?.__id);

        if (updatedAppointment) {
            const noteProps = {
                ptId: updatedAppointment?.patientId,
                oldValues: currAppointment,
                newValues: updatedAppointment,
                isRescheduled: true,
            };

            dispatch(addPatientSchedulerNote(noteProps));
        }
        return updatedAppointment;
    } catch (err) {
        console.log("ERROR RESCHEDULING", err)
        dispatch(setSnackbar({ type: 'error', message: 'Error during patient reschedule.' }))
    } finally {
        dispatch(setAgyleHeaderLoading(false));
        dispatch(getAppointmentSlots(true));
    }
}

export const findArrivedSlotAndUpdateToBeingSeen = (attendanceId, ptId) => async (dispatch, getState) => {
    const filterString = `patientId eq '${ptId}' AND (status eq '${SLOT_STATUSES.ARRIVED.value}' OR status eq '${SLOT_STATUSES.CALLED.value}') AND startDate ge_dt '${moment().startOf('day').format('YYYY-MM-DD HH:mm')}' AND startDate le_dt '${moment().add(2, 'days').startOf('day').format('YYYY-MM-DD HH:mm')}'`;

    const onSuccess = () => dispatch(setSnackbar({ type: 'success', message: `Patient appointment's status successfully changed to ${SLOT_STATUSES.BEEING_SEEN.name}` }));
    const onError = () => dispatch(setSnackbar({ type: 'error', message: `Could not update patient's appointment status to ${SLOT_STATUSES.BEEING_SEEN.name}` }));
    try {
        const response = await API.getFilteredAppointmentSlots(filterString);

        if (!response) {
            return;
        }

        const apiResponse = _.get(response, '__data');

        if (
            _.isArray(apiResponse) &&
            apiResponse.length === 1 &&
            _.isString(attendanceId) &&
            _.isString(ptId)
        ) {
            dispatch(updateAppointmentStatus(apiResponse.at(0)?.__id, SLOT_STATUSES.BEEING_SEEN.value, onSuccess, onError, null, attendanceId));
        }
    } catch (err) {
        console.log("ERROR:", err);
    }
}

export default appointmentSlice.reducer;
